/**
 * Auth API - login, register, MFA, password management.
 */
import { apiPost, apiGet, apiPut, apiPatch, apiDelete } from "./client";
import { collectDeviceSignals } from "@/lib/device-signals";
import type {
  User,
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  TokenPair,
  SSOConfigResponse,
  AuthSession,
  MfaMethod,
} from "@/types";

/** Public SSO settings (Entra client id, tenant - no secrets). */
export function getSSOConfig(portal?: "admin" | "practice"): Promise<SSOConfigResponse> {
  const qs = portal === "admin" ? "?portal=admin" : "";
  return apiGet<SSOConfigResponse>(`/auth/sso/config${qs}`);
}

/** Exchange Microsoft Entra id_token for EHR JWT tokens. */
export function loginWithEntra(id_token: string): Promise<LoginResponse> {
  return apiPost<LoginResponse>("/auth/sso/microsoft", {
    id_token,
    device: collectDeviceSignals(),
  });
}

/** Authenticate with email + password. Backend accepts "login" (email or username). */
export function login(req: LoginRequest): Promise<LoginResponse> {
  return apiPost<LoginResponse>("/auth/login", {
    login: req.email,
    password: req.password,
    device: collectDeviceSignals(),
  });
}

/** Complete login after multi-org picker (local password or Entra). */
export function selectOrg(
  selection_token: string,
  organization_id: string,
  authMode: "local" | "entra" = "local",
): Promise<LoginResponse> {
  const path =
    authMode === "entra"
      ? "/auth/sso/microsoft/select-org"
      : "/auth/login/select-org";
  return apiPost<LoginResponse>(path, {
    selection_token,
    organization_id,
    device: collectDeviceSignals(),
  });
}

/** Register a new patient account. */
export function register(req: RegisterRequest): Promise<User> {
  return apiPost<User>("/auth/register", req);
}

/** Exchange refresh cookie for a new token pair. */
export function refreshToken(): Promise<TokenPair> {
  return apiPost<TokenPair>("/auth/refresh", {});
}

/** Invalidate the current session. */
export function logout(): Promise<void> {
  return apiPost<void>("/auth/logout");
}

/** Resend email MFA code during login. */
export function resendMFAEmail(mfa_token: string): Promise<void> {
  return apiPost<void>("/auth/mfa/resend-email", { mfa_token });
}

/** Re-text the SMS login code during login. */
export function resendMFASMS(mfa_token: string): Promise<void> {
  return apiPost<void>("/auth/mfa/resend-sms", { mfa_token });
}

/** Begin MFA enrollment with setup_token from login (no JWT required). */
export function startMFASetup(
  setup_token: string,
  method: MfaMethod,
  phone?: string,
): Promise<{ method: string; secret?: string; qr_uri?: string; message?: string }> {
  return apiPost("/auth/mfa/setup/start", { setup_token, method, ...(phone ? { phone } : {}) });
}

/** Complete MFA enrollment and receive full JWT tokens. */
export function completeMFASetup(
  setup_token: string,
  code: string,
): Promise<LoginResponse & { backup_codes?: string[]; message?: string }> {
  return apiPost("/auth/mfa/setup/complete", {
    setup_token,
    code,
    device: collectDeviceSignals(),
  });
}

/** Complete MFA login: exchange mfa_token (from login) + TOTP/backup/email code for full tokens and user. */
export function verifyMFA(mfaToken: string, code: string): Promise<LoginResponse> {
  return apiPost<LoginResponse>("/auth/mfa/verify", {
    mfa_token: mfaToken,
    code,
    device: collectDeviceSignals(),
  });
}

/** Change the authenticated user's password. */
export function changePassword(
  current_password: string,
  new_password: string,
): Promise<void> {
  return apiPost<void>("/auth/change-password", { current_password, new_password });
}

/** Revoke all refresh tokens and invalidate access tokens for the current user. */
export function logoutAll(): Promise<void> {
  return apiPost<void>("/auth/logout-all");
}

/** List active sessions for the current user. */
export function listSessions(currentSessionId?: string | null): Promise<{ sessions: AuthSession[] }> {
  const qs = currentSessionId ? `?current_session_id=${encodeURIComponent(currentSessionId)}` : "";
  return apiGet<{ sessions: AuthSession[] }>(`/auth/sessions${qs}`);
}

/** Revoke one session by ID. Pass currentSessionId when revoking this device. */
export function revokeSession(sessionId: string, currentSessionId?: string | null): Promise<void> {
  const qs = currentSessionId ? `?current_session_id=${encodeURIComponent(currentSessionId)}` : "";
  return apiDelete<void>(`/auth/sessions/${sessionId}${qs}`);
}

/** Request a password-reset or welcome email. */
export function requestPasswordReset(email: string, purpose?: "reset" | "welcome"): Promise<void> {
  return apiPost<void>("/auth/forgot-password", purpose ? { email, purpose } : { email });
}

/** Reset password using a token from the reset email. */
export function resetPassword(
  token: string,
  new_password: string,
): Promise<void> {
  return apiPost<void>("/auth/reset-password", { token, new_password });
}

/** Confirm a new login device from the security email link. */
export function confirmDevice(token: string): Promise<void> {
  return apiPost<void>("/auth/devices/confirm", { token });
}

/** Reject an unrecognized login device from the security email link. */
export function rejectDevice(token: string): Promise<void> {
  return apiPost<void>("/auth/devices/reject", { token });
}

/** Get the currently authenticated user's profile (GET /auth/profile or /auth/me). */
export function getProfile(): Promise<User> {
  return apiGet<User>("/auth/profile");
}

export interface UpdateProfileRequest {
  first_name: string;
  last_name: string;
  middle_name?: string;
  title?: string;
  suffix?: string;
  phone?: string;
  avatar_url?: string;
  npi?: string;
  specialty?: string;
  taxonomy?: string;
  bio?: string;
  appointment_color?: string;
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
  emergency_contact_relationship?: string;
  products?: string[];
  availability?: string;
  preferences?: string;
}

/** Update the authenticated user's profile. */
export function updateProfile(req: UpdateProfileRequest): Promise<User> {
  return apiPut<User>("/auth/profile", req);
}

/** Merge a partial preference document without replacing unrelated settings. */
export function updatePreferences(
  preferences: Record<string, unknown>,
): Promise<User> {
  return apiPatch<User>("/auth/preferences", { preferences });
}
